import React from 'react';
import joi from 'joi';
import { Alert, TextField, Theme, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { colorPalette, useQlfTranslation } from '@qualifio/ui-commons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons/faLightbulbOn';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { AccessRuleFormValue } from '../../../earning-rules/form';
import { LanguageTabs } from '../../../shared/language-tabs';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRuleRTELabel } from '../../../rules/rule-table-constants';

interface Props {
  type: RuleType;
}

const styles = ({ spacing }: Theme) => {
  return {
    title: {
      fontWeight: 'bold',
      marginTop: `${spacing(3)} !important`,
      marginBottom: `${spacing(2)} !important`,
    },
    errorIcon: {
      marginLeft: spacing(0.5),
    },
    helper: {
      marginTop: spacing(2),
      padding: spacing(2),
      '& a': {
        textDecoration: 'none !important',
        color: colorPalette.blue.main,
      },
    },
  };
};
const useStyles = makeStyles(styles);

export function AccessRedirectForm({ type }: Props): React.ReactElement {
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const rteLabels = useRuleRTELabel();
  const classes = useStyles();
  const { program } = useProgram();
  const { features } = useFeatures();
  const { write } = features[Feature.PROGRAM_ACCESS];
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext<AccessRuleFormValue>();

  const criteria = watch('criteria');
  const criteriaIsEmpty =
    !criteria || !joi.object({ and: joi.array().length(0) }).validate(criteria).error;
  const disabled = !write || criteriaIsEmpty;

  const placeHolder = !write
    ? t('shared.action.permission.required')
    : criteriaIsEmpty
    ? t('access-rules.redirect-url.no-criteria.placeholder')
    : rteLabels[type];

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {t('access-rules.properties.redirect-url')}
        {!!errors.i18nAttributes && (
          <FontAwesomeIcon className={classes.errorIcon} color="red" icon={faExclamationCircle} />
        )}
      </Typography>

      <LanguageTabs
        languages={program.languages}
        message={t('access-rules.redirect-url.description')}
        errors={errors.i18nAttributes}
      >
        {({ language }) => (
          <div key={language}>
            <Controller
              control={control}
              name={`i18nAttributes.${language}.redirectUrl`}
              shouldUnregister={false}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <>
                  <TextField
                    disabled={disabled}
                    {...field}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    placeholder={placeHolder}
                  />
                </>
              )}
            />
          </div>
        )}
      </LanguageTabs>
      <Alert
        severity="warning"
        className={classes.helper}
        icon={<FontAwesomeIcon icon={faLightbulbOn} />}
      >
        {htmlT('access-rules.redirect-url.helper')}
      </Alert>
    </>
  );
}
